import { useState } from "react";
import { ChevronDown, Scale } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { InfoTip } from "@/components/app/Common";
import { WEIGHT_KEYS, SUBWEIGHT_KEYS, weightsTotal, weightsValid, subTotal, normalizeWeights, toggleStage } from "@/pages/wizard/jobUtils";
import { cn } from "@/lib/utils";

const STAGES = [
  ["personality", "Personality assessment", "A short questionnaire compared against the ideal profile.", (j) => j.assessment_config?.personality_enabled],
  ["work_sample", "Work sample", "A practical task that mirrors the job. Off by default.", (j) => j.work_sample_config?.enabled],
  ["interview", "AI interview", "A structured, recorded interview with follow-up questions.", (j) => j.interview_config?.enabled],
];

export function StagesEditor({ job, setJob }) {
  return (
    <section className="panel divide-y divide-line" data-testid="stages-editor">
      <div className="px-6 py-4"><h3 className="text-base font-semibold">Stages</h3><p className="text-sm text-ink-secondary">Resume screening always runs. Choose what happens next.</p></div>
      {STAGES.map(([key, label, text, isOn]) => (
        <label key={key} className="flex cursor-pointer items-center justify-between gap-4 px-6 py-4">
          <span><span className="block text-sm font-medium text-ink">{label}</span><span className="block text-sm text-ink-secondary">{text}</span></span>
          <Switch checked={!!isOn(job)} onCheckedChange={(on) => setJob((j) => toggleStage(j, key, on))} data-testid={`stage-switch-${key}`} />
        </label>
      ))}
    </section>
  );
}

const NumberCell = ({ value, onChange, disabled, testId, suffix = "%" }) => (
  <div className="flex items-center gap-1">
    <Input type="number" min={0} max={100} value={value} disabled={disabled} onChange={(e) => onChange(Number(e.target.value || 0))} className="h-8 w-20 text-right text-sm" data-testid={testId} />
    <span className="text-xs text-ink-muted">{suffix}</span>
  </div>
);

function Weights({ job, setJob }) {
  const w = job.scoring_weights || {};
  const off = { personality: !job.assessment_config?.personality_enabled, interview: !job.interview_config?.enabled, work_sample: !job.work_sample_config?.enabled };
  const setW = (k, pct) => setJob((j) => ({ ...j, scoring_weights: { ...j.scoring_weights, [k]: Math.round(pct) / 100 } }));
  const ok = weightsValid(w);
  return (
    <div className="space-y-3" data-testid="weights-editor">
      <h4 className="flex items-center gap-1.5 text-sm font-semibold">Scoring weights <InfoTip text="How much each stage counts towards the final score. Weights must add up to 100%." /></h4>
      {WEIGHT_KEYS.filter(([k]) => k !== "work_sample" || !off.work_sample).map(([k, label, tip]) => (
        <div key={k} className="flex items-center justify-between gap-3">
          <span className={cn("flex items-center gap-1.5 text-sm", off[k] ? "text-ink-muted" : "text-ink")}>{label} <InfoTip text={tip} /></span>
          <NumberCell value={Math.round((w[k] || 0) * 100)} disabled={off[k]} onChange={(v) => setW(k, v)} testId={`weight-${k}`} />
        </div>
      ))}
      <div className="flex items-center justify-between border-t border-line pt-3">
        <span className={cn("text-sm font-medium", ok ? "text-success" : "text-risk")} data-testid="weights-total">Total {Math.round(weightsTotal(w) * 100)}%{!ok && " · must be 100%"}</span>
        <Button variant="ghost" size="sm" className="text-brand" data-testid="weights-rebalance-btn" onClick={() => setJob((j) => ({ ...j, scoring_weights: normalizeWeights(j.scoring_weights) }))}>
          <Scale size={14} /> Rebalance
        </Button>
      </div>
    </div>
  );
}

function Subweights({ job, setJob }) {
  const s = job.resume_subweights || {};
  const total = subTotal(s);
  return (
    <div className="space-y-3" data-testid="subweights-editor">
      <h4 className="flex items-center gap-1.5 text-sm font-semibold">Resume score weights <InfoTip text="The resume score is the weighted sum of these four sub-scores. Must add up to 100%." /></h4>
      {SUBWEIGHT_KEYS.map(([k, label, tip]) => (
        <div key={k} className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-1.5 text-sm text-ink">{label} <InfoTip text={tip} /></span>
          <NumberCell value={s[k] ?? 0} onChange={(v) => setJob((j) => ({ ...j, resume_subweights: { ...j.resume_subweights, [k]: v } }))} testId={`subweight-${k}`} />
        </div>
      ))}
      <p className={cn("border-t border-line pt-3 text-sm font-medium", total === 100 ? "text-success" : "text-risk")} data-testid="subweights-total">Total {total}%{total !== 100 && " · must be 100%"}</p>
    </div>
  );
}

function Thresholds({ job, setJob }) {
  const t = job.thresholds || {};
  const setT = (k, v) => setJob((j) => ({ ...j, thresholds: { ...j.thresholds, [k]: v } }));
  return (
    <div className="space-y-3" data-testid="thresholds-editor">
      <h4 className="text-sm font-semibold">Thresholds</h4>
      <div className="flex items-center justify-between"><span className="flex items-center gap-1.5 text-sm">Strong Fit from <InfoTip text="Resume Match at or above this score is categorised Strong Fit." /></span>
        <NumberCell value={t.shortlist_min_fit ?? 75} suffix="" onChange={(v) => setT("shortlist_min_fit", v)} testId="threshold-shortlist" /></div>
      <div className="flex items-center justify-between"><span className="flex items-center gap-1.5 text-sm">Potential Fit from <InfoTip text="Scores from here up to the Strong Fit line are Potential Fit; below it is Not a Fit. Nobody is auto-rejected." /></span>
        <NumberCell value={t.review_band_min ?? 55} suffix="" onChange={(v) => setT("review_band_min", v)} testId="threshold-review" /></div>
      <label className="flex items-center justify-between pt-2"><span className="flex items-center gap-1.5 text-sm">Blind mode <InfoTip text="Hides names, photos and other personal details from reviewers to reduce bias." /></span>
        <Switch checked={!!job.blind_mode} onCheckedChange={(v) => setJob((j) => ({ ...j, blind_mode: v }))} data-testid="blind-mode-switch" /></label>
    </div>
  );
}

export function AdvancedSettings({ job, setJob, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="panel" data-testid="advanced-settings">
      <CollapsibleTrigger className="flex w-full items-center justify-between px-6 py-4 text-left" data-testid="advanced-settings-toggle">
        <span><span className="block text-base font-semibold">Advanced settings</span><span className="block text-sm text-ink-secondary">Scoring weights, thresholds and blind mode. The defaults work well.</span></span>
        <ChevronDown size={18} className={cn("text-ink-muted transition-transform duration-200", open && "rotate-180")} />
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up">
        <div className="grid gap-8 border-t border-line px-6 py-5 md:grid-cols-3">
          <Weights job={job} setJob={setJob} />
          <Subweights job={job} setJob={setJob} />
          <Thresholds job={job} setJob={setJob} />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
